import { useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  UploadCloud,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API, api, formatApiError } from "@/lib/api";

export default function BulkImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {headers:[], rows:[]}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const onFile = async (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("Please select a .csv file.");
      return;
    }
    setFile(f);
    setResult(null);
    // Local preview — first 5 data rows
    try {
      const text = await f.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const headers = lines[0]?.split(",").map((s) => s.trim().replace(/^"|"$/g, "")) || [];
      const rows = lines
        .slice(1, 6)
        .map((l) => l.split(",").map((s) => s.trim().replace(/^"|"$/g, "")));
      setPreview({ headers, rows, totalLines: lines.length - 1 });
    } catch {
      setPreview(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const onSubmit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/leads/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.created > 0) {
        toast.success(`${r.data.created} leads imported.`);
        onImported?.(r.data);
      } else if (r.data.skipped_duplicates > 0) {
        toast.info("All rows were duplicates — nothing new imported.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDownloadSample = () => {
    const url = `${API}/admin/leads/sample.csv`;
    const token = localStorage.getItem("medix_admin_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "medadmits-bulk-import-sample.csv";
        a.click();
      })
      .catch(() => toast.error("Sample download failed"));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[#0A2540]" /> Bulk import leads
          </DialogTitle>
          <DialogDescription>
            Upload a CSV (max 500 rows, 2 MB). Duplicates by email/phone will be
            skipped automatically.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            <div className="flex items-center justify-between rounded-md border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <span>Don't have a template?</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onDownloadSample}
                className="h-7 gap-1 text-[#0A2540]"
                data-testid="bulk-sample-btn"
              >
                <Download className="h-3.5 w-3.5" /> Download sample CSV
              </Button>
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white px-6 py-10 text-center transition-colors hover:border-[#0A2540]"
              data-testid="bulk-drop-zone"
            >
              <UploadCloud className="h-10 w-10 text-slate-400" />
              <p className="mt-3 text-sm font-medium text-[#0A2540]">
                {file ? file.name : "Click or drag CSV here"}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB`
                  : "Headers: name, phone, email, neet_score, preferred_country (required)"}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="bulk-file-input"
              />
            </div>

            {preview && preview.headers.length > 0 && (
              <div className="rounded-md border border-slate-200 bg-white">
                <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-xs font-medium text-slate-700">
                  <span>Preview · {preview.totalLines} rows total</span>
                  <Button variant="ghost" size="sm" onClick={reset} className="h-6 px-2">
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="max-h-48 overflow-auto px-2 py-1">
                  <table className="w-full text-[11px] text-slate-700">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        {preview.headers.slice(0, 7).map((h) => (
                          <th key={h} className="px-2 py-1 text-left font-medium">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {row.slice(0, 7).map((c, j) => (
                            <td key={j} className="px-2 py-1">{c || "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {result && (
          <div className="space-y-3" data-testid="bulk-result">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-semibold">Import complete</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-700">
                    {result.created}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-600">
                    Created
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-700">
                    {result.skipped_duplicates}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-600">
                    Duplicates
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-rose-700">
                    {result.error_count}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-rose-600">
                    Errors
                  </div>
                </div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-40 overflow-auto rounded-md border border-rose-200 bg-rose-50/50 p-3">
                <div className="mb-2 flex items-center gap-1 text-xs font-medium text-rose-800">
                  <AlertCircle className="h-3 w-3" /> Row errors
                </div>
                <ul className="space-y-1 text-xs text-rose-700">
                  {result.errors.map((e, i) => (
                    <li key={i}>
                      Row {e.row}: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleClose(false)} className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              data-testid="bulk-close">Close</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => handleClose(false)}
                data-testid="bulk-cancel">Cancel</Button>
              <Button onClick={onSubmit} disabled={!file || submitting}
                className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                data-testid="bulk-submit">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing…</> : `Import ${preview?.totalLines || ""} rows`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
